import React, { useState, useEffect } from 'react';
import { Heart, HeartOff } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../contexts/ToastContext';

interface FavoriteButtonProps {
  bookId: string;
  isFavorited?: boolean;
  favoriteCount?: number;
  onToggle?: (isFavorited: boolean, newCount: number) => void;
  size?: 'sm' | 'md' | 'lg';
  showCount?: boolean;
}

const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  bookId,
  isFavorited: initialIsFavorited = false,
  favoriteCount: initialCount = 0,
  onToggle,
  size = 'md',
  showCount = true
}) => {
  const [isFavorited, setIsFavorited] = useState(initialIsFavorited);
  const [favoriteCount, setFavoriteCount] = useState(initialCount);
  const [isLoading, setIsLoading] = useState(false);
  const { showFavorite, showError } = useToast();

  // Check initial favorite status
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      try {
        const response = await axios.get(`/api/favorites/check/${bookId}`);
        setIsFavorited(response.data.isFavorited);
      } catch (error) {
        console.error('Error checking favorite status:', error);
      }
    };

    checkFavoriteStatus();
  }, [bookId]);

  // Get favorite count
  useEffect(() => {
    const getFavoriteCount = async () => {
      try {
        const response = await axios.get(`/api/favorites/count/${bookId}`);
        setFavoriteCount(response.data.count);
      } catch (error) {
        console.error('Error getting favorite count:', error);
      }
    };

    getFavoriteCount();
  }, [bookId]);

  const handleToggle = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      if (isFavorited) {
        // Remove from favorites
        await axios.delete(`/api/favorites/${bookId}`);
        setIsFavorited(false);
        setFavoriteCount(prev => Math.max(0, prev - 1));
        onToggle?.(false, favoriteCount - 1);
        showFavorite('Favori retiré', 'Le livre a été retiré de vos favoris');
      } else {
        // Add to favorites
        await axios.post(`/api/favorites/${bookId}`);
        setIsFavorited(true);
        setFavoriteCount(prev => prev + 1);
        onToggle?.(true, favoriteCount + 1);
        showFavorite('Ajouté aux favoris', 'Le livre a été ajouté à vos favoris');
      }
    } catch (error: any) {
      console.error('Error toggling favorite:', error);
      showError('Erreur', 'Impossible de modifier les favoris');
    } finally {
      setIsLoading(false);
    }
  };

  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  };

  const buttonSizeClasses = {
    sm: 'p-1',
    md: 'p-2',
    lg: 'p-3'
  };

  return (
    <div className="flex items-center space-x-1">
      <button
        onClick={handleToggle}
        disabled={isLoading}
        className={`
          ${buttonSizeClasses[size]}
          rounded-full transition-all duration-200
          ${isFavorited 
            ? 'bg-red-100 text-red-500 hover:bg-red-200' 
            : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-red-400'
          }
          ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          flex items-center justify-center
        `}
        title={isFavorited ? 'Retirer des favoris' : 'Ajouter aux favoris'}
      >
        {isFavorited ? (
          <Heart className={`${sizeClasses[size]} fill-current`} />
        ) : (
          <HeartOff className={sizeClasses[size]} />
        )}
      </button>
      
      {showCount && favoriteCount > 0 && (
        <span className="text-xs text-gray-500">
          {favoriteCount}
        </span>
      )}
    </div>
  );
};

export default FavoriteButton;
